package com.arena_web.ui.components.events

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateEventRequest
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.SportDto
import com.arena_web.data.dto.UpdateEventRequest
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.forms.DatePicker
import com.arena_web.ui.components.forms.Dropdown
import com.arena_web.ui.components.forms.DropdownOption
import com.arena_web.ui.components.forms.TextInput
import com.arena_web.ui.components.forms.TimePicker
import com.arena_web.ui.components.forms.Toggle
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.overlay.SHEET_DRAG_AREA_CLASS
import com.arena_web.ui.components.widgets.CoinIcon
import com.arena_web.ui.components.widgets.formatCoins
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.DemoPlayer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.DemoPlayers
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.theme.toRgbaString
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.ui.components.widgets.MatchedText
import com.arena_web.ui.components.widgets.SuggestionMuted
import com.arena_web.utils.AddressPrediction
import com.arena_web.utils.LeafletMap
import com.arena_web.utils.autocompleteAddress
import com.arena_web.utils.cachedUserCoordinates
import com.arena_web.utils.getIpLocation
import com.arena_web.utils.getUserCoordinates
import com.arena_web.utils.resolveAddressPrediction
import com.arena_web.utils.reverseGeocode
import com.arena_web.utils.localToUtcIso
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

val PopupOverlayFadeIn = Keyframes {
    from { Modifier.opacity(0) }
    to { Modifier.opacity(1) }
}

val PopupSlideUp = Keyframes {
    from { Modifier.opacity(0).translateY(20.px) }
    to { Modifier.opacity(1).translateY(0.px) }
}

@Composable
fun CreateEventPopup(
    isOpen: Boolean,
    onClose: () -> Unit,
    onCreated: (EventDetailDto) -> Unit,
    editingEvent: EventDetailDto? = null,
) {
    if (!isOpen) return

    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    val sportColors = LocalSportColors.current
    val isEditing = editingEvent != null
    // Phones get the single-column bottom-sheet layout (Figma 8697:91508);
    // desktop keeps the centered two-column popup. Both share every field
    // component, the map, and the submit/validation logic below.
    val mobile = isMobile()

    // Pricing + wallet snapshot from the app-level cache so the Save/Create
    // button can show the live charge in coins ("Save Changes · 20 ⓒ") when
    // toggling the Pro flag triggers a payment. First open warms both caches.
    val pricing by container.pricing.collectAsState()
    val walletBalance by container.walletBalance.collectAsState()
    LaunchedEffect(Unit) {
        container.ensurePricingLoaded()
        container.ensureWalletLoaded()
    }

    // Decompose the event's UTC dateTime into local date/time strings once,
    // so reopening the popup on the same event reseeds the fields exactly.
    val editInitial = remember(editingEvent?.id) {
        editingEvent?.let {
            val start = kotlin.js.Date(it.dateTime)
            val end = it.endDateTime?.let { iso -> kotlin.js.Date(iso) }
            fun pad2(n: Int) = n.toString().padStart(2, '0')
            Triple(
                "${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}",
                "${pad2(start.getHours())}:${pad2(start.getMinutes())}",
                end?.let { d -> "${pad2(d.getHours())}:${pad2(d.getMinutes())}" } ?: ""
            )
        }
    }

    // Form fields — when editing, seed from the existing event so the form
    // opens with the user's saved values instead of empty defaults.
    var title by remember(editingEvent?.id) { mutableStateOf(editingEvent?.title ?: "") }
    var sportId by remember(editingEvent?.id) {
        mutableStateOf(editingEvent?.sportId ?: (Storage.get(StorageKeys.SELECTED_SPORT_ID) ?: ""))
    }
    var customSportName by remember(editingEvent?.id) { mutableStateOf(editingEvent?.customSportName ?: "") }
    var isPrivate by remember(editingEvent?.id) {
        mutableStateOf(editingEvent?.privacy?.uppercase() == "PRIVATE")
    }
    var isPro by remember(editingEvent?.id) { mutableStateOf(editingEvent?.isPro ?: false) }
    var format by remember(editingEvent?.id) { mutableStateOf(editingEvent?.format?.toString() ?: "") }
    var maxPlayers by remember(editingEvent?.id) { mutableStateOf(editingEvent?.maxPlayers?.toString() ?: "") }
    var cost by remember(editingEvent?.id) {
        val current = editingEvent?.cost?.trim()
        val n = current?.toDoubleOrNull()
        mutableStateOf(if (current == null || n == null || n == 0.0) "" else current)
    }
    var date by remember(editingEvent?.id) {
        val seeded = editInitial?.first ?: run {
            val now = kotlin.js.Date()
            val y = now.getFullYear()
            val m = (now.getMonth() + 1).toString().padStart(2, '0')
            val d = now.getDate().toString().padStart(2, '0')
            "$y-$m-$d"
        }
        mutableStateOf(seeded)
    }
    var timeFrom by remember(editingEvent?.id) { mutableStateOf(editInitial?.second ?: "") }
    var timeTo by remember(editingEvent?.id) { mutableStateOf(editInitial?.third ?: "") }
    var address by remember(editingEvent?.id) { mutableStateOf(editingEvent?.address ?: "") }
    var latitude by remember(editingEvent?.id) { mutableStateOf(editingEvent?.latitude ?: "") }
    var longitude by remember(editingEvent?.id) { mutableStateOf(editingEvent?.longitude ?: "") }
    var city by remember(editingEvent?.id) { mutableStateOf<String?>(editingEvent?.city) }
    var addressQuery by remember(editingEvent?.id) { mutableStateOf(editingEvent?.address ?: "") }
    var addressSuggestions by remember { mutableStateOf<List<AddressPrediction>>(emptyList()) }
    var showSuggestions by remember { mutableStateOf(false) }
    var mapCenterKey by remember { mutableStateOf(0) }
    // Debounce for the drag → reverse-geocode sync: successive map drags
    // cancel the pending lookup (Nominatim policy is 1 req/s).
    var mapReverseJob by remember { mutableStateOf<Job?>(null) }
    // Mobile: the map isn't inline; a button on the address field opens it as a
    // full-screen picker overlay.
    var showMapPicker by remember { mutableStateOf(false) }

    // Image upload state
    var imageBytes by remember { mutableStateOf<ByteArray?>(null) }
    var imageFileName by remember { mutableStateOf("") }
    var imageContentType by remember { mutableStateOf("") }
    var imagePreviewUrl by remember { mutableStateOf<String?>(null) }

    // UI state — sport list comes from the app-level cache (`sportsList` on
    // AppContainer). The previous build hit `GET /sports` every time the popup
    // opened which added ~300ms of latency on the edit flow; the cache is
    // already warmed up by the Arena page so the dropdown renders instantly.
    val cachedSports by container.sportsList.collectAsState()
    var sports by remember { mutableStateOf<List<SportDto>>(emptyList()) }
    var sportDropdownExpanded by remember { mutableStateOf(false) }
    var formatDropdownExpanded by remember { mutableStateOf(false) }
    var showPrivateInfo by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var fieldErrors by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    // Sync local sports from the cache. If the cache is warm we render
    // immediately; if it's cold (e.g. user landed straight on /events), we
    // trigger the same `ensureSportsLoaded()` the rest of the app uses, then
    // pick it up via the StateFlow.
    LaunchedEffect(cachedSports) {
        if (cachedSports.isNotEmpty()) sports = cachedSports
    }
    LaunchedEffect(Unit) {
        if (cachedSports.isEmpty()) container.ensureSportsLoaded()
    }

    // User's detected location — used to rank the address autocomplete (their
    // country + nearby first), and (on a fresh create) to drop the default pin.
    // Seeded from the session geo cache (warmed at page load in AppEntry) so
    // the very first autocomplete keystroke is already biased; the
    // LaunchedEffect below may upgrade IP-derived coordinates to a GPS fix.
    var userGeoLat by remember { mutableStateOf(cachedUserCoordinates()?.latitude) }
    var userGeoLng by remember { mutableStateOf(cachedUserCoordinates()?.longitude) }
    var userCountryName by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(Unit) {
        // Editing already has a location; don't prompt GPS — the search bias
        // falls back to the event's own coordinates below.
        if (isEditing) return@LaunchedEffect
        val geo = getUserCoordinates()
        // IP lookup (cached) gives the country name (to rank own-country results
        // first) plus a coordinate fallback when GPS is denied.
        val ip = getIpLocation()
        userCountryName = ip?.country
        val pt = when {
            geo != null -> geo.latitude to geo.longitude
            ip != null -> ip.lat to ip.lng
            else -> null
        } ?: return@LaunchedEffect
        userGeoLat = pt.first
        userGeoLng = pt.second
        // Default pin only on a fresh create with nothing chosen yet. Positions
        // the map only — does NOT fill the address field.
        if (latitude.isBlank() && address.isBlank()) {
            latitude = pt.first.toString()
            longitude = pt.second.toString()
            mapCenterKey++
        }
    }

    // Debounced address search. Gated on a fully-chosen `address` (not bare
    // coords) so autocomplete still works after the default geo pin sets lat/lng.
    // Google Places via the backend proxy, Nominatim (own-country-first,
    // nearest-first ranking) when Google yields nothing — see
    // utils/AddressAutocomplete.kt.
    LaunchedEffect(addressQuery) {
        if (addressQuery.length < 3 || address.isNotBlank()) {
            addressSuggestions = emptyList()
            return@LaunchedEffect
        }
        delay(300)
        addressSuggestions = autocompleteAddress(
            placesApi = container.placesApi,
            query = addressQuery,
            biasLat = userGeoLat ?: latitude.toDoubleOrNull(),
            biasLng = userGeoLng ?: longitude.toDoubleOrNull(),
            userCountryName = userCountryName,
        )
        showSuggestions = addressSuggestions.isNotEmpty()
    }

    // ── Address autocomplete composable (reused in both layouts) ──
    // showMapButton (mobile) adds a trailing map icon that opens the picker
    // overlay, since the mobile sheet has no inline map.
    val addressField: @Composable (Boolean) -> Unit = { showMapButton ->
        val suggestionsOpen = showSuggestions && addressSuggestions.isNotEmpty()
        // Click-away dismiss — under the expanded surface (99 < 100).
        if (suggestionsOpen) {
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(99)
                    .onClick { showSuggestions = false }
            )
        }
        Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
            // Label
            FieldLabel(t.eventAddressLabel, required = true)
            // Unified expanding surface (PersonalInfoEditModal city pattern).
            // Desktop: the outer Box reserves the 44px slot and the inner Box
            // grows as an ABSOLUTE overlay over the content below. Mobile
            // (showMapButton layout): the expansion is IN-FLOW and pushes the
            // form down — parity with the mobile app's address field.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(
                        if (showMapButton) Modifier
                        else Modifier.sHeight(44).styleModifier { property("position", "relative") }
                    )
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sMinHeight(44)
                        .sBorderRadius(28)
                        .then(
                            if (showMapButton) Modifier
                            else Modifier.position(Position.Absolute).zIndex(if (suggestionsOpen) 100 else 10)
                        )
                        .styleModifier {
                            if (!showMapButton) {
                                property("top", "0")
                                property("left", "0")
                            }
                            property("border", if (fieldErrors.containsKey("address")) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                            property("box-sizing", "border-box")
                        }
                ) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        // Pill input row with the pin icon
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(44)
                                .styleModifier {
                                    property("padding-left", s(12))
                                    property("padding-right", s(8))
                                    property("box-sizing", "border-box")
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                src = AppIcons.Location,
                                modifier = Modifier.sSize(16).styleModifier { property("opacity", "0.5") }
                            )
                            org.jetbrains.compose.web.dom.Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                                value(addressQuery)
                                onInput { e ->
                                    val v = e.value
                                    addressQuery = v
                                    fieldErrors = fieldErrors - "address"
                                    if (address.isNotBlank() && v != address) {
                                        latitude = ""
                                        longitude = ""
                                        city = null
                                        address = ""
                                    }
                                }
                                attr("placeholder", t.eventAddressPlaceholder)
                                style {
                                    property("flex", "1")
                                    property("min-width", "0")
                                    property("background", "transparent")
                                    property("border", "none")
                                    property("outline", "none")
                                    property("font-size", s(16))
                                    property("font-family", "inherit")
                                    property("color", "#bed6f5")
                                    property("margin-left", s(4))
                                    property("padding", "0")
                                }
                            }
                            // Trailing map button — opens the full-screen map picker.
                            if (showMapButton) {
                                Box(
                                    modifier = Modifier
                                        .sSize(32)
                                        .flexShrink(0)
                                        .sBorderRadius(100)
                                        .cursor(Cursor.Pointer)
                                        .onClick { showMapPicker = true }
                                        .styleModifier {
                                            property("display", "flex")
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(
                                        src = AppIcons.Map,
                                        modifier = Modifier.sSize(16).styleModifier {
                                            property("filter", AppColors.IconDefaultFilter)
                                        }
                                    )
                                }
                            }
                        }

                        if (suggestionsOpen) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .sHeight(1)
                                    .sMarginBottom(6)
                                    .styleModifier {
                                        property("background", "rgba(255, 255, 255, 0.06)")
                                    }
                            )
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .styleModifier {
                                        property("padding", "0 ${s(6)} ${s(6)}")
                                        property("max-height", "240px")
                                        property("overflow-y", "auto")
                                        property("scrollbar-width", "thin")
                                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                    }
                            ) {
                                addressSuggestions.forEach { prediction ->
                                    Column(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .sPadding(8, 6)
                                            .sBorderRadius(8)
                                            .cursor(Cursor.Pointer)
                                            .onClick {
                                                // Google predictions carry no coordinates —
                                                // one details call resolves them (and closes
                                                // the billing session). Nominatim hits return
                                                // instantly from the same helper.
                                                showSuggestions = false
                                                addressQuery = prediction.title
                                                scope.launch {
                                                    val result =
                                                        resolveAddressPrediction(container.placesApi, prediction)
                                                            ?: return@launch
                                                    address = listOfNotNull(
                                                        result.street,
                                                        result.houseNumber,
                                                        result.city,
                                                        result.country
                                                    ).filter { it.isNotBlank() }.joinToString(", ")
                                                    addressQuery = result.title
                                                    latitude = result.lat.toString()
                                                    longitude = result.lng.toString()
                                                    city = result.city
                                                    mapCenterKey++
                                                }
                                            }
                                    ) {
                                        MatchedText(prediction.title, addressQuery.trim())
                                        if (prediction.subtitle.isNotBlank()) {
                                            SpanText(
                                                prediction.subtitle,
                                                modifier = Modifier
                                                    .sFontSize(12)
                                                    .color(SuggestionMuted)
                                                    .styleModifier {
                                                        property("overflow", "hidden")
                                                        property("text-overflow", "ellipsis")
                                                        property("white-space", "nowrap")
                                                        property("margin-top", "2px")
                                                    }
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            // Error
            fieldErrors["address"]?.let { err ->
                SpanText(err, modifier = Modifier.sFontSize(12).color(AppColors.Error))
            }
        }
    }

    // ── Map view (shared by both layouts) ──
    // The Leaflet instance keys off a single DOM id, so only one layout may
    // mount it at a time — which is exactly what `if (mobile) … else …` below
    // guarantees. The init / recenter / dispose effects live inside the lambda
    // so they run with whichever layout renders it.
    val mapView: @Composable (String) -> Unit = { heightCss ->
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("height", heightCss) }
                .sBorderRadius(12)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border", "0.5px solid rgba(255,255,255,0.1)")
                    // Leaflet fills the box with absolutely-positioned panes, so
                    // the container's own min-content height is ~0. Inside the
                    // mobile sheet's flex scroll column that lets flex-shrink
                    // collapse the map to nothing — pin the height.
                    property("flex-shrink", "0")
                }
                .id("create-event-map")
        ) {}

        LaunchedEffect(Unit) {
            delay(200)
            // Prefer the currently-chosen coordinates (autocomplete pick or a
            // prior map pick) so the pin is already on the selected spot; then
            // the editing event's saved location; finally the user's geo / world.
            val curLat = latitude.toDoubleOrNull()
            val curLng = longitude.toDoubleOrNull()
            val editLat = editingEvent?.latitude?.toDoubleOrNull()
            val editLng = editingEvent?.longitude?.toDoubleOrNull()
            when {
                curLat != null && curLng != null -> {
                    LeafletMap.init("create-event-map", curLat, curLng, zoom = 15)
                }
                editLat != null && editLng != null -> {
                    LeafletMap.init("create-event-map", editLat, editLng, zoom = 14)
                }
                else -> {
                    val geo = getUserCoordinates()
                    if (geo != null) {
                        LeafletMap.init("create-event-map", geo.latitude, geo.longitude, zoom = 13)
                    } else {
                        LeafletMap.init("create-event-map", 30.0, 10.0, zoom = 2)
                    }
                }
            }
            LeafletMap.onMoveEnd { centerLat, centerLng ->
                latitude = centerLat.toString()
                longitude = centerLng.toString()
                fieldErrors = fieldErrors - "address"
                // Dragging the map re-picks the location — reverse-geocode the
                // new centre into the address field. Programmatic recenters
                // (autocomplete pick → setView) don't fire this callback, so
                // the two directions can't loop.
                mapReverseJob?.cancel()
                mapReverseJob = scope.launch {
                    delay(600)
                    val r = reverseGeocode(centerLat, centerLng) ?: return@launch
                    address = listOfNotNull(r.street, r.houseNumber, r.city, r.country)
                        .filter { it.isNotBlank() }.joinToString(", ")
                    addressQuery = r.title
                    city = r.city
                }
            }
        }
        LaunchedEffect(mapCenterKey) {
            if (mapCenterKey > 0 && latitude.isNotBlank() && longitude.isNotBlank()) {
                LeafletMap.setView(latitude.toDouble(), longitude.toDouble())
            }
        }
        DisposableEffect(Unit) {
            onDispose { LeafletMap.destroy() }
        }
    }

    // ── PRO toggle pill + info icon (shared header trailing control) ──
    // Pro tier requires a known sport; "Other" custom sports get a disabled
    // (dimmed, non-interactive) pill so the user sees Pro is unavailable.
    val proPill: @Composable () -> Unit = {
        val isOtherSport = sports.find { it.id == sportId }?.name == "Other"
        Row(
            modifier = Modifier.sGap(4),
            verticalAlignment = Alignment.Top
        ) {
            Box(
                modifier = Modifier
                    .position(Position.Relative)
                    .sHeight(44)
                    .sBorderRadius(100)
                    .sPadding(6, 8)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("box-sizing", "border-box")
                        if (isOtherSport) property("opacity", "0.45")
                    }
            ) {
                Row(
                    modifier = Modifier
                        .sGap(14)
                        .styleModifier { property("height", "100%") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        modifier = Modifier
                            .sGap(4)
                            .then(
                                if (isOtherSport) Modifier.cursor(Cursor.NotAllowed)
                                else Modifier.cursor(Cursor.Pointer).onClick {
                                    isPro = !isPro
                                    // Dropping PRO drops the format requirement.
                                    if (!isPro) fieldErrors = fieldErrors - "format"
                                }
                            ),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier
                                .sSize(16)
                                .styleModifier {
                                    property("background-color", sportColors.primary.toRgbString())
                                    property("mask-image", "url('${AppIcons.Diamond}')")
                                    property("-webkit-mask-image", "url('${AppIcons.Diamond}')")
                                    property("mask-size", "contain")
                                    property("-webkit-mask-size", "contain")
                                    property("mask-position", "center")
                                    property("-webkit-mask-position", "center")
                                    property("mask-repeat", "no-repeat")
                                    property("-webkit-mask-repeat", "no-repeat")
                                }
                        )
                        SpanText(
                            t.eventProBadge,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.SemiBold)
                                .color(sportColors.primary)
                        )
                        SpanText(
                            "+",
                            modifier = Modifier
                                .sFontSize(13)
                                .fontWeight(FontWeight.SemiBold)
                                .color(sportColors.primary)
                                .styleModifier {
                                    property("margin-left", "1px")
                                    property("transform", "translateY(-5px)")
                                }
                        )
                    }
                    Toggle(
                        checked = isPro,
                        onCheckedChange = {
                            if (!isOtherSport) {
                                isPro = it
                                if (!it) fieldErrors = fieldErrors - "format"
                            }
                        },
                    )
                }
            }
            Icon(
                src = AppIcons.InfoCircle,
                modifier = Modifier
                    .sSize(14)
                    .styleModifier {
                        property("opacity", "0.6")
                        property("margin-top", s(4))
                    }
            )
        }
    }

    // ── Pricing-derived charge info (shared) ──
    // Pro toggle triggers a payment on creation (new Pro event) or when editing
    // flips false → true. The button label is suffixed with the live charge.
    val willChargeForPro = isPro && (!isEditing || editingEvent?.isPro == false)
    val proPriceNumber = pricing?.proEvent?.toDoubleOrNull() ?: 0.0
    val balanceNumber = walletBalance?.toDoubleOrNull()
    val cannotAfford = willChargeForPro && proPriceNumber > 0.0 &&
        balanceNumber != null && balanceNumber < proPriceNumber
    // Pro charge is denominated in wallet coins; ModalCreateButton renders it
    // wallet-style after the label — coin glyph first, then "× 20".
    val proCoinPrice = if (willChargeForPro && proPriceNumber > 0.0 && !isLoading) {
        formatCoins(pricing?.proEvent ?: "20")
    } else null
    val createButtonText = when {
        isLoading && isEditing -> t.eventSaving
        isLoading -> t.eventCreating
        isEditing -> t.eventSaveChanges
        else -> t.eventCreateEvent
    }
    // Insufficient balance → open the wallet (BuyCoinsDialog handles mobile sheet
    // vs desktop popup itself).
    var walletOpen by remember { mutableStateOf(false) }

    // ── Validate + submit (shared by both layouts) ──
    val submit: () -> Unit = onClick@{
        // Stop the user before any image upload / API call if they don't have
        // enough for the Pro charge. The backend re-checks atomically, but a
        // client-side gate gives a cleaner error and avoids a wasted upload.
        if (cannotAfford) {
            walletOpen = true
            return@onClick
        }
        // Validate
        val errors = mutableMapOf<String, String>()
        if (title.isBlank()) errors["title"] = t.eventErrorTitleRequired
        if (sportId.isBlank()) errors["sportId"] = t.eventErrorSportRequired
        val selectedSportNameValidation = sports.find { it.id == sportId }?.name
        if (selectedSportNameValidation == "Other" && customSportName.isBlank()) {
            errors["customSportName"] = t.eventErrorSportNameRequired
        }
        // Format is required for Pro events (squads and the live formation
        // depend on it); optional otherwise — stored as null when left blank
        // (see format = format.toIntOrNull()).
        if (isPro && format.isBlank()) errors["format"] = t.eventErrorFormatRequiredPro
        if (timeFrom.isBlank()) errors["timeFrom"] = t.eventErrorStartTimeRequired
        if (timeTo.isBlank()) errors["timeTo"] = t.eventErrorEndTimeRequired
        fun parseMinutes(t: String): Int? = t.split(":").let {
            it.getOrNull(0)?.toIntOrNull()?.times(60)?.plus(it.getOrNull(1)?.toIntOrNull() ?: 0)
        }
        val fromMinutes = if (timeFrom.isNotBlank()) parseMinutes(timeFrom) else null
        val toMinutes = if (timeTo.isNotBlank()) parseMinutes(timeTo) else null
        if (fromMinutes != null && toMinutes != null && toMinutes <= fromMinutes) {
            errors["timeTo"] = t.eventErrorEndAfterStart
        }
        // Block starting an event in the past when today is chosen. When editing
        // an event whose start hasn't changed we skip this so reopening on a
        // past event still lets the user save other field changes.
        if (fromMinutes != null && !errors.containsKey("timeFrom")) {
            val originalIso = editingEvent?.dateTime
            val newIso = localToUtcIso(date, timeFrom)
            if (!isEditing || newIso != originalIso) {
                val now = kotlin.js.Date()
                val todayStr = "${now.getFullYear()}-" +
                    "${(now.getMonth() + 1).toString().padStart(2, '0')}-" +
                    now.getDate().toString().padStart(2, '0')
                if (date == todayStr) {
                    val nowMinutes = now.getHours() * 60 + now.getMinutes()
                    if (fromMinutes < nowMinutes) {
                        errors["timeFrom"] = t.eventErrorStartInPast
                    }
                }
            }
        }
        // City only comes from autocomplete suggestions; the map is purely for
        // refining coordinates. An event without a city is invalid, so
        // distinguish the two failure modes.
        if (latitude.isBlank() || longitude.isBlank()) {
            errors["address"] = t.eventErrorSelectAddress
        } else if (city.isNullOrBlank()) {
            errors["address"] = t.eventErrorAddressCity
        }
        // In edit mode the existing cover stands in until the user explicitly
        // replaces it, so a fresh upload isn't required.
        if (!isEditing && imageBytes == null) errors["image"] = t.eventErrorCoverRequired

        if (errors.isNotEmpty()) {
            fieldErrors = errors
            return@onClick
        }

        val repo = container.eventRepository
        if (repo == null) {
            error = if (isEditing) t.eventErrorSignInEdit else t.eventErrorSignInCreate
            return@onClick
        }

        isLoading = true
        error = null

        scope.launch {
            // Upload image first if selected
            var uploadedImageUrl: String? = null
            if (imageBytes != null) {
                repo.uploadEventImage(imageBytes!!, imageFileName, imageContentType)
                    .onSuccess { uploadedImageUrl = it }
                    .onFailure { e ->
                        isLoading = false
                        error = e.message ?: t.eventErrorUploadImage
                        return@launch
                    }
            }

            val isOtherSport = sports.find { it.id == sportId }?.name == "Other"

            if (isEditing && editingEvent != null) {
                // chatId stays read-only after creation; everything else
                // (including sport + custom sport name) can be changed.
                // customSportName is sent as "" when the sport is no longer
                // "Other" so the backend clears the stale name.
                repo.updateEvent(
                    editingEvent.id,
                    UpdateEventRequest(
                        title = title.trim(),
                        sportId = sportId,
                        customSportName = if (isOtherSport) customSportName.trim() else "",
                        privacy = if (isPrivate) "PRIVATE" else "PUBLIC",
                        format = format.toIntOrNull(),
                        maxPlayers = maxPlayers.toIntOrNull(),
                        cost = cost.ifBlank { "0.00" },
                        imageUrl = uploadedImageUrl,
                        dateTime = localToUtcIso(date, timeFrom),
                        endDateTime = if (timeTo.isNotBlank()) localToUtcIso(date, timeTo) else null,
                        latitude = latitude.trim(),
                        longitude = longitude.trim(),
                        address = address.trim(),
                        city = city,
                        isPro = isPro,
                    )
                ).onSuccess { event ->
                    isLoading = false
                    onCreated(event)
                    onClose()
                }.onFailure { e ->
                    isLoading = false
                    error = e.message ?: t.eventErrorSaveChanges
                }
            } else {
                repo.createEvent(
                    CreateEventRequest(
                        title = title.trim(),
                        sportId = sportId,
                        customSportName = if (isOtherSport) customSportName.trim() else null,
                        privacy = if (isPrivate) "PRIVATE" else "PUBLIC",
                        format = format.toIntOrNull(),
                        maxPlayers = maxPlayers.toIntOrNull(),
                        cost = cost.ifBlank { "0.00" },
                        imageUrl = uploadedImageUrl,
                        dateTime = localToUtcIso(date, timeFrom),
                        endDateTime = if (timeTo.isNotBlank()) localToUtcIso(date, timeTo) else null,
                        latitude = latitude.trim(),
                        longitude = longitude.trim(),
                        address = address.trim(),
                        city = city,
                        chatId = null,
                        isPro = isPro
                    )
                ).onSuccess { event ->
                    isLoading = false
                    onCreated(event)
                    onClose()
                }.onFailure { e ->
                    isLoading = false
                    error = e.message ?: t.eventErrorCreateEvent
                }
            }
        }
    }

    // ── Mobile: single-column bottom sheet (Figma 8697:91508) ──
    if (mobile) {
        BottomSheet(onDismiss = onClose, contentGap = 20, fullHeight = true, dragHeaderOnly = true) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("min-height", "0")
                    }
            ) {
                // Header: title + PRO pill + faint divider. Marked as the sheet's
                // drag zone — with dragHeaderOnly, pulling the form only scrolls.
                Column(modifier = Modifier.fillMaxWidth().sGap(12).classNames(SHEET_DRAG_AREA_CLASS)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            if (isEditing) t.eventEditEventTitle else t.eventCreateEvent,
                            modifier = Modifier
                                .sFontSize(20)
                                .fontWeight(FontWeight.Medium)
                                .color(AppColors.Text.Secondary)
                        )
                        proPill()
                    }
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(1)
                            .styleModifier {
                                property(
                                    "background",
                                    "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                                )
                            }
                    )
                }

                // Only the form below the header scrolls; the clip edge is
                // the divider itself — the 20px lead-in is INSIDE the scroll
                // content (padding-top), so it slides away with the form.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sGap(20)
                        .styleModifier {
                            property("flex", "1")
                            property("min-height", "0")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                            property("padding-top", "20px")
                        }
                ) {

                // Error banner
                error?.let { err ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(12, 16)
                            .backgroundColor(Color("rgba(255, 26, 26, 0.1)"))
                            .styleModifier { property("border", "1px solid rgba(255, 26, 26, 0.3)") },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(err, modifier = Modifier.sFontSize(14).color(AppColors.Error))
                    }
                }

                // Cover photo
                ImagePickerArea(
                    previewUrl = imagePreviewUrl ?: editingEvent?.imageUrl,
                    error = fieldErrors["image"],
                    onClick = {
                        pickImageFile { bytes, name, type ->
                            imageBytes = bytes
                            imageFileName = name
                            imageContentType = type
                            imagePreviewUrl = createObjectUrl(bytes, type)
                            fieldErrors = fieldErrors - "image"
                        }
                    }
                )

                // Address — with a trailing map button (opens the picker overlay).
                addressField(true)

                // Title
                ModalInput(
                    value = title,
                    onValueChange = { title = it; fieldErrors = fieldErrors - "title" },
                    label = t.eventTitleLabel,
                    required = true,
                    placeholder = t.eventTitlePlaceholder,
                    error = fieldErrors["title"]
                )

                // Sport + Max Players
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    verticalAlignment = Alignment.Top
                ) {
                    SportSelectorField(
                        sportId = sportId,
                        sports = sports,
                        expanded = sportDropdownExpanded,
                        onToggle = { sportDropdownExpanded = !sportDropdownExpanded },
                        onSportSelected = { sport ->
                            sportId = sport.id
                            fieldErrors = fieldErrors - "sportId"
                            if (sport.name != "Other") customSportName = ""
                            if (sport.name == "Other") isPro = false
                            val allowed = formatOptionsForSport(sport.name).map { it.value }
                            if (format.isNotBlank() && format !in allowed) format = ""
                        },
                        error = fieldErrors["sportId"],
                        inline = true,
                        modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                    )
                    ModalInput(
                        value = maxPlayers,
                        onValueChange = { maxPlayers = it },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0px") },
                        label = t.eventMaxPlayersLabel,
                        placeholder = t.eventMaxPlayersPlaceholder
                    )
                }

                // Custom sport name (only for "Other")
                if (sports.find { it.id == sportId }?.name == "Other") {
                    ModalInput(
                        value = customSportName,
                        onValueChange = { customSportName = it; fieldErrors = fieldErrors - "customSportName" },
                        label = t.eventSportNameLabel,
                        required = true,
                        placeholder = t.eventSportNamePlaceholder,
                        error = fieldErrors["customSportName"]
                    )
                }

                // Cost — half-width, the other half stays empty
                Row(modifier = Modifier.fillMaxWidth().sGap(12)) {
                    CostInput(
                        value = cost,
                        onValueChange = { cost = it },
                        modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                    )
                    Box(modifier = Modifier.styleModifier { property("flex", "1 1 0px") })
                }

                // Format — horizontal scroll strip with the selected chip centred.
                FormatScrollPicker(
                    format = format,
                    sportName = sports.find { it.id == sportId }?.name,
                    onFormatSelected = { format = it; fieldErrors = fieldErrors - "format" },
                    error = fieldErrors["format"],
                    required = isPro,
                )

                // Date
                DatePicker(
                    selectedDate = date,
                    onDateSelected = { date = it; fieldErrors = fieldErrors - "dateTime" },
                    label = t.eventDateLabel,
                    required = true,
                    eventStyle = true
                )

                // Time From + To
                Row(modifier = Modifier.fillMaxWidth().sGap(12)) {
                    TimePicker(
                        value = timeFrom,
                        onValueChange = { timeFrom = it; fieldErrors = fieldErrors - "timeFrom" },
                        label = t.eventTimeFromLabel,
                        required = true,
                        error = fieldErrors["timeFrom"],
                        modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                    )
                    TimePicker(
                        value = timeTo,
                        onValueChange = { timeTo = it; fieldErrors = fieldErrors - "timeTo" },
                        label = t.eventTimeToLabel,
                        required = true,
                        error = fieldErrors["timeTo"],
                        modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                    )
                }

                // Private toggle (full-width row, toggle pushed to the right)
                PrivateField(
                    isPrivate = isPrivate,
                    onPrivateChange = { isPrivate = it },
                    isPro = isPro,
                    showInfo = showPrivateInfo,
                    onToggleInfo = { showPrivateInfo = !showPrivateInfo },
                    onDismissInfo = { showPrivateInfo = false },
                    spread = true
                )

                // Create / Save button (full width)
                ModalCreateButton(
                    text = createButtonText,
                    enabled = !isLoading,
                    fullWidth = true,
                    coinPrice = proCoinPrice,
                    onClick = submit
                )

                // Trailing spacer — padding-bottom on a flex scroll container is
                // dropped at scroll-end on Chrome/Safari; a sized Div holds the gap.
                Box(modifier = Modifier.fillMaxWidth().sHeight(8))
                }
            }
        }

        // Insufficient balance → the full "My Wallet" / buy-coins sheet (same as
        // the avatar flow). Rendered as a SIBLING of the create-event sheet and
        // layered above it (z-index 1200 > the sheet's 1100).
        com.arena_web.ui.components.overlay.BuyCoinsDialog(
            isOpen = walletOpen,
            onClose = { walletOpen = false },
            walletBalance = walletBalance,
            zIndex = 1200,
        )

        // ── Map picker (mobile) — a bottom sheet ──
        // Sibling of the create-event sheet. Leaflet stops touchmove propagation
        // on its container, so panning the map doesn't drag the sheet. Drag the
        // map so the centre pin sits on the spot, then Confirm → reverse-geocode.
        if (showMapPicker) {
            BottomSheet(onDismiss = { showMapPicker = false }, contentGap = 12, fullHeight = true) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sGap(12)
                        .styleModifier {
                            property("flex", "1")
                            property("min-height", "0")
                        }
                ) {
                    SpanText(
                        t.eventChooseOnMap,
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.Text.Secondary)
                    )
                    // Map fills the rest of the sheet.
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .flexGrow(1)
                            .position(Position.Relative)
                            .styleModifier { property("min-height", "0") }
                    ) {
                        mapView("100%")
                    }
                    // Confirm
                    ModalCreateButton(
                        text = t.eventConfirmLocation,
                        enabled = true,
                        fullWidth = true,
                        onClick = {
                            val la = latitude.toDoubleOrNull()
                            val ln = longitude.toDoubleOrNull()
                            if (la == null || ln == null) {
                                showMapPicker = false
                            } else {
                                scope.launch {
                                    reverseGeocode(la, ln)?.let { r ->
                                        address = listOfNotNull(r.street, r.houseNumber, r.city, r.country)
                                            .filter { it.isNotBlank() }.joinToString(", ")
                                        addressQuery = r.title
                                        latitude = r.lat.toString()
                                        longitude = r.lng.toString()
                                        city = r.city
                                        fieldErrors = fieldErrors - "address"
                                    }
                                    showMapPicker = false
                                }
                            }
                        }
                    )
                    // Bottom gap so the Confirm button isn't flush with the edge.
                    Box(modifier = Modifier.fillMaxWidth().sHeight(16))
                }
            }
        }
        return
    }

    // ── Overlay ──
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // ── Popup card ──
        Column(
            modifier = Modifier
                .sMaxWidth(1050)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(20)
                .overflow(Overflow.Hidden)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("border", "0.5px solid transparent")
                    property(
                        "background",
                        "linear-gradient(180deg, #09142D, #09142D) padding-box, " +
                            "linear-gradient(0deg, #09142D, #09142D) padding-box "
                    )
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                    property("max-height", "90vh")
                }
        ) {
            // ── Header ──
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(20, 24),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom
            ) {
                SpanText(
                    if (isEditing) t.eventEditEventTitle else t.eventCreateEvent,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Secondary)
                )
                // Pro toggle pill — Figma 8630:524792 (shared with mobile).
                proPill()
            }

            // ── Scrollable body ──
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("overflow-y", "auto")
                        property("max-height", "calc(90vh - 140px)")
                    }
            ) {
                // Error banner
                error?.let { err ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPadding(0, 24)
                            .sPaddingTop(16),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sBorderRadius(10)
                                .sPadding(12, 16)
                                .backgroundColor(Color("rgba(255, 26, 26, 0.1)"))
                                .styleModifier {
                                    property("border", "1px solid rgba(255, 26, 26, 0.3)")
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                err,
                                modifier = Modifier.sFontSize(14).color(AppColors.Error)
                            )
                        }
                    }
                }

                // ── Two columns with vertical divider ──
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPadding(24)
                        .styleModifier { property("align-items", "stretch") }
                ) {
                    // ── Left column: form fields ──
                    Column(
                        modifier = Modifier
                            .styleModifier { property("flex", "1 1 0px") }
                            .sGap(24)
                    ) {
                        // Image picker (taller, blue dashed). In edit mode the
                        // saved cover URL acts as the initial preview so the
                        // user sees what's currently on the event.
                        ImagePickerArea(
                            previewUrl = imagePreviewUrl ?: editingEvent?.imageUrl,
                            error = fieldErrors["image"],
                            onClick = {
                                pickImageFile { bytes, name, type ->
                                    imageBytes = bytes
                                    imageFileName = name
                                    imageContentType = type
                                    imagePreviewUrl = createObjectUrl(bytes, type)
                                    fieldErrors = fieldErrors - "image"
                                }
                            }
                        )

                        // Address
                        addressField(false)

                        // Title
                        ModalInput(
                            value = title,
                            onValueChange = { title = it; fieldErrors = fieldErrors - "title" },
                            label = t.eventTitleLabel,
                            required = true,
                            placeholder = t.eventTitlePlaceholder,
                            error = fieldErrors["title"]
                        )

                        // Sport + Max Players
                        Row(
                            modifier = Modifier.fillMaxWidth().sGap(20),
                            verticalAlignment = Alignment.Top
                        ) {
                            SportSelectorField(
                                sportId = sportId,
                                sports = sports,
                                expanded = sportDropdownExpanded,
                                onToggle = { sportDropdownExpanded = !sportDropdownExpanded },
                                onSportSelected = { sport ->
                                    sportId = sport.id
                                    fieldErrors = fieldErrors - "sportId"
                                    if (sport.name != "Other") customSportName = ""
                                    // Pro tier is reserved for known sports —
                                    // "Other" custom sports can't be Pro, drop
                                    // the flag if user pivots after enabling it.
                                    if (sport.name == "Other") isPro = false
                                    val allowed = formatOptionsForSport(sport.name).map { it.value }
                                    if (format.isNotBlank() && format !in allowed) format = ""
                                },
                                error = fieldErrors["sportId"],
                                modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                            )
                            ModalInput(
                                value = maxPlayers,
                                onValueChange = { maxPlayers = it },
                                modifier = Modifier.styleModifier { property("flex", "1 1 0px") },
                                label = t.eventMaxPlayersLabel,
                                placeholder = t.eventMaxPlayersPlaceholder
                            )
                        }

                        // Custom sport name
                        if (sports.find { it.id == sportId }?.name == "Other") {
                            ModalInput(
                                value = customSportName,
                                onValueChange = { customSportName = it; fieldErrors = fieldErrors - "customSportName" },
                                label = t.eventSportNameLabel,
                                required = true,
                                placeholder = t.eventSportNamePlaceholder,
                                error = fieldErrors["customSportName"]
                            )
                        }

                        // Cost — half-width, the other half stays empty
                        Row(modifier = Modifier.fillMaxWidth().sGap(20)) {
                            CostInput(
                                value = cost,
                                onValueChange = { cost = it },
                                modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                            )
                            Box(modifier = Modifier.styleModifier { property("flex", "1 1 0px") })
                        }

                        // Format (own row, half-width to match design)
                        Row(modifier = Modifier.fillMaxWidth().sGap(20)) {
                            FormatSelectorField(
                                format = format,
                                sportName = sports.find { it.id == sportId }?.name,
                                expanded = formatDropdownExpanded,
                                onToggle = { formatDropdownExpanded = !formatDropdownExpanded },
                                onFormatSelected = {
                                    format = it
                                    fieldErrors = fieldErrors - "format"
                                },
                                error = fieldErrors["format"],
                                required = isPro,
                                modifier = Modifier.styleModifier { property("flex", "1 1 0px") }
                            )
                            Box(modifier = Modifier.styleModifier { property("flex", "1 1 0px") })
                        }
                    }

                    // ── Vertical divider ──
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("width", "1px")
                                property("align-self", "stretch")
                                property("margin", "0 ${s(20)}")
                                property("background", "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)")
                            }
                    )

                    // ── Right column: map + date/time + private ──
                    Column(
                        modifier = Modifier
                            .styleModifier { property("flex", "1 1 0px") }
                            .sGap(24)
                    ) {
                        // Map (shared with mobile — single Leaflet instance).
                        mapView(s(200))

                        // Date picker
                        DatePicker(
                            selectedDate = date,
                            onDateSelected = { date = it; fieldErrors = fieldErrors - "dateTime" },
                            label = t.eventDateLabel,
                            required = true,
                            eventStyle = true,
                            // Desktop popup: 7-day window paged by arrows (no
                            // scroll strip). The mobile sheet keeps the swipe strip.
                            scrollable = false
                        )

                        // Time From + To
                        Row(modifier = Modifier.fillMaxWidth().sGap(12)) {
                            TimePicker(
                                value = timeFrom,
                                onValueChange = { timeFrom = it; fieldErrors = fieldErrors - "timeFrom" },
                                label = t.eventTimeFromLabel,
                                required = true,
                                error = fieldErrors["timeFrom"]
                            )
                            TimePicker(
                                value = timeTo,
                                onValueChange = { timeTo = it; fieldErrors = fieldErrors - "timeTo" },
                                label = t.eventTimeToLabel,
                                required = true,
                                error = fieldErrors["timeTo"]
                            )
                        }

                        // Private toggle
                        PrivateField(
                            isPrivate = isPrivate,
                            onPrivateChange = { isPrivate = it },
                            isPro = isPro,
                            showInfo = showPrivateInfo,
                            onToggleInfo = { showPrivateInfo = !showPrivateInfo },
                            onDismissInfo = { showPrivateInfo = false }
                        )
                    }
                }
            }

            // ── Footer ──
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(16, 24)
                    .sGap(12),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                ModalCancelButton(
                    onClick = { onClose() },
                    enabled = !isLoading
                )
                // Pro toggle triggers a payment on creation (new Pro event) or
                // when editing flips false → true. The button label carries the
                // live charge (priceSuffix) and the submit lambda gates on
                // affordability — both computed once above and shared with mobile.
                ModalCreateButton(
                    text = createButtonText,
                    enabled = !isLoading,
                    coinPrice = proCoinPrice,
                    onClick = submit
                )

                // Insufficient balance → open the wallet (desktop popup).
                com.arena_web.ui.components.overlay.BuyCoinsDialog(
                    isOpen = walletOpen,
                    onClose = { walletOpen = false },
                    walletBalance = walletBalance,
                )
            }
        }
    }
}

/**
 * Viewport offset of the nearest ancestor that establishes a containing block for
 * `position: fixed` descendants — i.e. one with a `transform`, `will-change:
 * transform`, `filter`, or `perspective`. The mobile BottomSheet panel has such a
 * transform, so fixed children are positioned relative to IT, not the viewport.
 * Returns (0,0) when there's none (e.g. the desktop popup) → fixed == viewport.
 */
private fun fixedContainingBlockOffset(el: org.w3c.dom.HTMLElement): Pair<Double, Double> {
    var node: org.w3c.dom.Element? = el.parentElement
    while (node != null) {
        val cs = kotlinx.browser.window.getComputedStyle(node)
        val transform = cs.getPropertyValue("transform")
        val willChange = cs.getPropertyValue("will-change")
        val filter = cs.getPropertyValue("filter")
        val perspective = cs.getPropertyValue("perspective")
        val establishesCb = (transform.isNotBlank() && transform != "none") ||
            willChange.contains("transform") ||
            (filter.isNotBlank() && filter != "none") ||
            (perspective.isNotBlank() && perspective != "none")
        if (establishesCb) {
            val r = node.getBoundingClientRect()
            return r.top to r.left
        }
        node = node.parentElement
    }
    return 0.0 to 0.0
}

@Composable
private fun ImagePickerArea(
    previewUrl: String?,
    error: String? = null,
    onClick: () -> Unit
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(165)
                .sBorderRadius(22)
                .cursor(Cursor.Pointer)
                .overflow(Overflow.Hidden)
                .onClick { onClick() }
                .styleModifier {
                    if (previewUrl == null) {
                        property("border", if (error != null) "2px dashed ${AppColors.Error}" else "2px dashed ${sportColors.primary.toRgbString()}")
                        property("background", "linear-gradient(193deg, ${sportColors.primary.toRgbaString(0.06f)} 49%, rgba(5, 4, 12, 0.06) 106%)")
                    } else {
                        property("border", "1px solid rgba(255, 255, 255, 0.1)")
                    }
                },
            contentAlignment = Alignment.Center
        ) {
            if (previewUrl != null) {
                Image(
                    src = previewUrl,
                    modifier = Modifier
                        .fillMaxWidth()
                        .fillMaxHeight()
                        .objectFit(ObjectFit.Cover)
                )
            } else {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.sGap(10)
                ) {
                    // Plus icon in circle outline
                    Box(
                        modifier = Modifier
                            .sSize(48)
                            .sBorderRadius(24)
                            .styleModifier {
                                if (error != null) {
                                    property("border", "1.5px solid ${AppColors.Error}")
                                } else {
                                    property("border", "0.3px solid transparent")
                                    property(
                                        "background",
                                        "${AppGradients.InteractiveSecondary} padding-box, " +
                                                "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                                    ) }
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "+",
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(FontWeight.Normal)
                                .color(if (error != null) AppColors.Error else Color("#99b1bf"))
                        )
                    }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            t.eventAddPhoto,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.Medium)
                                .color(if (error != null) AppColors.Error else Color("#99b1bf"))
                        )
                        SpanText(
                            " *",
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.Medium)
                                .color(AppColors.Primary)
                        )
                    }
                }
            }
        }
        if (error != null) {
            SpanText(
                error,
                modifier = Modifier.sFontSize(12).color(AppColors.Error)
            )
        }
    }
}

private const val MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB

private fun pickImageFile(onFilePicked: (ByteArray, String, String) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/jpeg,image/png,image/webp"
    input.onchange = { _ ->
        val file = input.files?.get(0)
        if (file != null) {
            if (file.size.toInt() > MAX_FILE_SIZE_BYTES) {
                kotlinx.browser.window.alert("File is too large. Maximum size is 50 MB.")
            } else {
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    val bytes = int8Array.unsafeCast<ByteArray>()
                    onFilePicked(bytes, file.name, file.type)
                }
                reader.readAsArrayBuffer(file)
            }
        }
    }
    input.click()
}

private fun createObjectUrl(bytes: ByteArray, contentType: String): String {
    val jsArray = bytes.unsafeCast<org.khronos.webgl.Int8Array>()
    val uint8 = org.khronos.webgl.Uint8Array(jsArray.buffer)
    val blob = org.w3c.files.Blob(arrayOf(uint8), org.w3c.files.BlobPropertyBag(contentType))
    return org.w3c.dom.url.URL.createObjectURL(blob)
}

/** Round-ball glyph for a sport name — mirrors SportDropdownPanel's icon set. */
private fun sportGlyph(name: String): String = when (name.lowercase()) {
    "football" -> AppIcons.SportFootball
    "basketball" -> AppIcons.SportBasketball
    "volleyball" -> AppIcons.SportVolleyball
    "tennis" -> AppIcons.SportTennis
    "padel" -> AppIcons.SportPadel
    else -> AppIcons.SportOther
}

@Composable
private fun SportSelectorField(
    sportId: String,
    sports: List<SportDto>,
    expanded: Boolean,
    onToggle: () -> Unit,
    onSportSelected: (SportDto) -> Unit,
    error: String? = null,
    // true → the options expand in-flow right below the trigger (mobile sheet);
    // false → a floating fixed dropdown (desktop popup).
    inline: Boolean = false,
    modifier: Modifier = Modifier
) {
    val selectedSport = sports.find { it.id == sportId }
    val sportName = selectedSport?.name ?: ""
    val sportTriggerId = remember { "sport-trigger" }
    var dropdownTop by remember { mutableStateOf(0.0) }
    var dropdownLeft by remember { mutableStateOf(0.0) }
    var dropdownWidth by remember { mutableStateOf(0.0) }
    val t = LocalAppStrings.current

    // Calculate position when expanded
    LaunchedEffect(expanded) {
        if (expanded) {
            val el = document.getElementById(sportTriggerId) as? org.w3c.dom.HTMLElement
            if (el != null) {
                val rect = el.getBoundingClientRect()
                // `position: fixed` is resolved against the nearest transformed
                // ancestor (the BottomSheet panel uses transform / will-change), so
                // viewport coords from getBoundingClientRect must be made relative
                // to that containing block — otherwise the menu drops far too low.
                val (cbTop, cbLeft) = fixedContainingBlockOffset(el)
                dropdownTop = rect.bottom + 4.0 - cbTop
                dropdownLeft = rect.left - cbLeft
                dropdownWidth = rect.width
            }
        }
    }

    Column(modifier = modifier.sGap(6)) {
        FieldLabel(t.eventSportLabel, required = true)

        // Trigger content (sport glyph + label + chevron) — shared by both layouts.
        val triggerInner: @Composable () -> Unit = {
            if (sportName.isNotBlank()) {
                Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                    // Sport glyph, tinted with the sport's own accent colour.
                    Box(
                        modifier = Modifier.sSize(16).styleModifier {
                            property("background-color", SportColors.forSport(sportName.lowercase()).primary.toString())
                            property("-webkit-mask-image", "url(\"${sportGlyph(sportName)}\")")
                            property("mask-image", "url(\"${sportGlyph(sportName)}\")")
                            property("-webkit-mask-size", "contain")
                            property("mask-size", "contain")
                            property("-webkit-mask-repeat", "no-repeat")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-position", "center")
                            property("mask-position", "center")
                        }
                    )
                    SpanText(
                        sportName.replaceFirstChar { it.uppercase() },
                        modifier = Modifier.sFontSize(16).color(AppColors.Text.Secondary)
                    )
                }
            } else {
                SpanText(
                    if (sports.isEmpty()) t.eventLoading else t.eventSelectSportPlaceholder,
                    modifier = Modifier.sFontSize(16).color(Color("rgba(153,177,191,0.5)"))
                )
            }
            Icon(src = AppIcons.ChevronDown)
        }
        // Hide the already-selected sport from the options list.
        val sportOptions = sports.filter { it.id != sportId }

        // Custom rows so the list matches the trigger exactly — same glyph size
        // (14), text size (16), left inset (12) and gap (8). (Not SportDropdownPanel,
        // whose 32-px icon boxes / own scale wouldn't line up with the trigger.)
        val optionRows: @Composable () -> Unit = {
            Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
                sportOptions.forEach { sport ->
                    val name = sport.name
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(10, 12)
                            .sGap(8)
                            .cursor(Cursor.Pointer)
                            .onClick { onSportSelected(sport); onToggle() },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier.sSize(18).styleModifier {
                                property("background-color", SportColors.forSport(name.lowercase()).primary.toString())
                                property("-webkit-mask-image", "url(\"${sportGlyph(name)}\")")
                                property("mask-image", "url(\"${sportGlyph(name)}\")")
                                property("-webkit-mask-size", "contain")
                                property("mask-size", "contain")
                                property("-webkit-mask-repeat", "no-repeat")
                                property("mask-repeat", "no-repeat")
                                property("-webkit-mask-position", "center")
                                property("mask-position", "center")
                            }
                        )
                        SpanText(
                            name.replaceFirstChar { it.uppercase() },
                            modifier = Modifier.sFontSize(16).color(AppColors.Text.Secondary)
                        )
                    }
                }
            }
        }

        if (inline) {
            // Accordion that OVERLAYS the form: the 44px slot stays in flow so
            // nothing below shifts, while the field container is absolute and
            // grows downward over the fields beneath it (City-field pattern).
            val open = expanded && sports.isNotEmpty()
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .styleModifier { property("position", "relative") }
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .position(Position.Absolute)
                        .zIndex(if (open) 100 else 0)
                        .sBorderRadius(if (open) 16 else 28)
                        .styleModifier {
                            property("top", "0")
                            property("left", "0")
                            property("border", if (error != null) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                    "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                            property("overflow", "hidden")
                            if (open) property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        }
                ) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(44)
                                .cursor(Cursor.Pointer)
                                .styleModifier { property("padding", "0 ${s(12)}") }
                                .onClick { onToggle() },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) { triggerInner() }
                        if (open) {
                            Box(
                                modifier = Modifier.fillMaxWidth().sPaddingBottom(6).styleModifier {
                                    property("max-height", "260px")
                                    property("overflow-y", "auto")
                                    property("scrollbar-width", "none")
                                }
                            ) { optionRows() }
                        }
                    }
                }
            }
        } else {
            // Pill trigger + floating fixed dropdown (desktop popup).
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .sBorderRadius(28)
                    .cursor(Cursor.Pointer)
                    .id(sportTriggerId)
                    .styleModifier {
                        property("border", if (error != null) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("padding", "0 ${s(12)}")
                    }
                    .onClick { onToggle() },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) { triggerInner() }

            if (expanded && sports.isNotEmpty()) {
                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .top(0.px).left(0.px).right(0.px).bottom(0.px)
                        .zIndex(1300)
                        .onClick { onToggle() }
                )

                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .zIndex(1301)
                        .sBorderRadius(16)
                        .sPadding(8)
                        .styleModifier {
                            property("top", "${dropdownTop}px")
                            property("left", "${dropdownLeft}px")
                            property("width", "${dropdownWidth}px")
                            property("max-height", "260px")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                            property("border", "0.3px solid transparent")
                            property(
                                "background",
                                "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                    "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                            property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        }
                ) { optionRows() }
            }
        }

        if (error != null) {
            SpanText(error, modifier = Modifier.sFontSize(12).color(AppColors.Error))
        }
    }
}

/**
 * Format picker as a horizontal scroll strip with the selected chip centred.
 * Swipe / drag to browse; tapping a chip selects it and re-centres. Leading /
 * trailing spacers let the first / last chip reach the middle too.
 */
@Composable
private fun FormatScrollPicker(
    format: String,
    sportName: String?,
    onFormatSelected: (String) -> Unit,
    error: String? = null,
    // Pro events require a format; regular ones leave it optional.
    required: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val options = formatOptionsForSport(sportName)
    val scrollId = remember {
        "fmt-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}"
    }
    // Touch uses the browser's own smooth, momentum scrolling. Only add mouse
    // drag-to-scroll for desktop / narrow windows.
    DisposableEffect(scrollId) {
        val doc = kotlinx.browser.document
        fun strip() = doc.getElementById(scrollId)
        var down = false; var mStartX = 0.0; var mStartScroll = 0.0
        val onDown: (org.w3c.dom.events.Event) -> Unit = { e ->
            val s = strip(); val t = e.target as? org.w3c.dom.Node
            if (s != null && t != null && s.contains(t)) {
                down = true; mStartX = e.asDynamic().clientX as Double; mStartScroll = s.asDynamic().scrollLeft as Double
            }
        }
        val onMove: (org.w3c.dom.events.Event) -> Unit = { e ->
            if (down) strip()?.let { s ->
                s.asDynamic().scrollLeft = mStartScroll - ((e.asDynamic().clientX as Double) - mStartX); e.preventDefault()
            }
        }
        val onUp: (org.w3c.dom.events.Event) -> Unit = { down = false }
        doc.addEventListener("mousedown", onDown)
        doc.addEventListener("mousemove", onMove)
        doc.addEventListener("mouseup", onUp)
        onDispose {
            doc.removeEventListener("mousedown", onDown)
            doc.removeEventListener("mousemove", onMove)
            doc.removeEventListener("mouseup", onUp)
        }
    }

    Column(modifier = modifier.fillMaxWidth().sGap(6)) {
        FieldLabel(t.eventFormatFieldLabel, required = required)
        // Left-aligned horizontal scroll of format chips (no field surround).
        Row(
            modifier = Modifier
                .id(scrollId)
                .fillMaxWidth()
                .sGap(8)
                .styleModifier {
                    property("overflow-x", "auto")
                    property("overflow-y", "hidden")
                    property("scrollbar-width", "none")
                    property("-ms-overflow-style", "none")
                    property("-webkit-overflow-scrolling", "touch")
                    property("cursor", "grab")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            options.forEach { opt ->
                val isSel = opt.value == format
                Box(
                    modifier = Modifier
                        .sBorderRadius(100)
                        .sPadding(8, 18)
                        .cursor(Cursor.Pointer)
                        // Tap toggles: tapping the selected chip clears the choice.
                        .onClick { onFormatSelected(if (isSel) "" else opt.value) }
                        .styleModifier {
                            property("flex-shrink", "0")
                            if (isSel) {
                                property("background", sportColors.primary.toString())
                            } else {
                                // Unselected = same surface as the input fields.
                                property("border", "0.3px solid transparent")
                                property(
                                    "background",
                                    "${AppGradients.InteractiveSecondary} padding-box, " +
                                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                                )
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        opt.label,
                        modifier = Modifier
                            .sFontSize(15)
                            .fontWeight(FontWeight.Medium)
                            .color(if (isSel) sportColors.onPrimary else AppColors.Text.Secondary)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }
        if (error != null) {
            SpanText(error, modifier = Modifier.sFontSize(12).color(AppColors.Error))
        }
    }
}

@Composable
private fun FormatSelectorField(
    format: String,
    sportName: String?,
    expanded: Boolean,
    onToggle: () -> Unit,
    onFormatSelected: (String) -> Unit,
    error: String? = null,
    inline: Boolean = false,
    // Pro events require a format; regular ones leave it optional.
    required: Boolean = false,
    modifier: Modifier = Modifier
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val options = formatOptionsForSport(sportName)
    val selectedLabel = options.find { it.value == format }?.label

    val formatTriggerId = remember { "format-trigger" }
    var dropdownTop by remember { mutableStateOf(0.0) }
    var dropdownLeft by remember { mutableStateOf(0.0) }
    var dropdownWidth by remember { mutableStateOf(0.0) }

    LaunchedEffect(expanded) {
        if (expanded) {
            val el = document.getElementById(formatTriggerId) as? org.w3c.dom.HTMLElement
            if (el != null) {
                val rect = el.getBoundingClientRect()
                // `position: fixed` is resolved against the nearest transformed
                // ancestor (the BottomSheet panel uses transform / will-change), so
                // viewport coords from getBoundingClientRect must be made relative
                // to that containing block — otherwise the menu drops far too low.
                val (cbTop, cbLeft) = fixedContainingBlockOffset(el)
                dropdownTop = rect.bottom + 4.0 - cbTop
                dropdownLeft = rect.left - cbLeft
                dropdownWidth = rect.width
            }
        }
    }

    Column(modifier = modifier.sGap(6)) {
        FieldLabel(t.eventFormatFieldLabel, required = required)

        // Trigger content (label + chevron) — shared by both layouts.
        val triggerInner: @Composable () -> Unit = {
            if (selectedLabel != null) {
                SpanText(selectedLabel, modifier = Modifier.sFontSize(16).color(AppColors.Text.Secondary))
            } else {
                SpanText(t.eventSelectPlaceholder, modifier = Modifier.sFontSize(16).color(Color("rgba(153,177,191,0.5)")))
            }
            Icon(src = AppIcons.ChevronDown)
        }

        // Option rows — shared by the inline accordion and the floating layout.
        val optionRows: @Composable () -> Unit = {
            Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
                options.forEach { option ->
                    val isSelected = option.value == format
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(12, 16)
                            .cursor(Cursor.Pointer)
                            .then(
                                if (isSelected) Modifier.styleModifier {
                                    property("background", sportColors.primary.toRgbaString(0.12f))
                                } else Modifier
                            )
                            .onClick {
                                onFormatSelected(option.value)
                                onToggle()
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            option.label,
                            modifier = Modifier.sFontSize(16).color(
                                if (isSelected) AppColors.Text.Primary
                                else AppColors.Text.Secondary
                            )
                        )
                    }
                }
            }
        }

        if (inline) {
            // Accordion overlay (City-field pattern): 44px slot stays in flow,
            // the field container is absolute and expands over the fields below.
            Box(
                modifier = Modifier.fillMaxWidth().sHeight(44).styleModifier { property("position", "relative") }
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .position(Position.Absolute)
                        .zIndex(if (expanded) 100 else 0)
                        .sBorderRadius(if (expanded) 16 else 28)
                        .styleModifier {
                            property("top", "0")
                            property("left", "0")
                            property("border", if (error != null) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                    "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                            property("overflow", "hidden")
                            if (expanded) property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        }
                ) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sHeight(44)
                                .cursor(Cursor.Pointer)
                                .styleModifier { property("padding", "0 ${s(12)}") }
                                .onClick { onToggle() },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) { triggerInner() }
                        if (expanded) {
                            Box(
                                modifier = Modifier.fillMaxWidth().sPadding(0, 8).sPaddingBottom(8).styleModifier {
                                    property("max-height", "260px")
                                    property("overflow-y", "auto")
                                    property("scrollbar-width", "none")
                                }
                            ) { optionRows() }
                        }
                    }
                }
            }
        } else {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .sBorderRadius(28)
                    .cursor(Cursor.Pointer)
                    .id(formatTriggerId)
                    .styleModifier {
                        property("border", if (error != null) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("padding", "0 ${s(12)}")
                    }
                    .onClick { onToggle() },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) { triggerInner() }

            if (expanded) {
                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .top(0.px).left(0.px).right(0.px).bottom(0.px)
                        .zIndex(1300)
                        .onClick { onToggle() }
                )

                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .zIndex(1301)
                        .sBorderRadius(16)
                        .sPadding(8)
                        .styleModifier {
                            property("top", "${dropdownTop}px")
                            property("left", "${dropdownLeft}px")
                            property("width", "${dropdownWidth}px")
                            property("max-height", "200px")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                            property("border", "0.3px solid transparent")
                            property(
                                "background",
                                "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                    "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                            property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        }
                ) { optionRows() }
            }
        }

        if (error != null) {
            SpanText(error, modifier = Modifier.sFontSize(12).color(AppColors.Error))
        }
    }
}

internal fun formatOptionsForSport(sportName: String?): List<DropdownOption> {
    val range = when (sportName?.lowercase()) {
        "football" -> 1..11
        "basketball" -> 1..5
        "tennis" -> 1..2
        "padel" -> 1..2
        "volleyball" -> 1..6
        else -> 1..11
    }
    return range.map { n ->
        DropdownOption(n.toString(), "${n}x${n}")
    }
}

/** Pill-shaped text input matching Figma modal design */
@Composable
private fun ModalInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    required: Boolean = false,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true
) {
    Column(modifier = modifier.fillMaxWidth().sGap(6)) {
        label?.let {
            FieldLabel(it, required = required)
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(44)
                .sBorderRadius(28)
                .styleModifier {
                    property("border", if (error != null) "0.3px solid ${AppColors.Error}" else "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("padding-left", s(12))
                    property("padding-right", s(8))
                    property("box-sizing", "border-box")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            org.jetbrains.compose.web.dom.Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                value(value)
                onInput { onValueChange(it.value) }
                if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
                if (!enabled) attr("disabled", "")
                style {
                    width(100.percent)
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("font-size", s(16))
                    property("font-family", "inherit")
                    property("color", "#bed6f5")
                    property("padding", "0")
                }
            }
        }
        error?.let {
            SpanText(it, modifier = Modifier.sFontSize(12).color(AppColors.Error))
        }
    }
}

/** Field label with optional red asterisk for required fields. */
@Composable
private fun FieldLabel(text: String, required: Boolean = false) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.styleModifier { property("padding-left", s(4)) }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(14)
                .color(AppColors.Text.Secondary)
        )
        if (required) {
            SpanText(
                " *",
                modifier = Modifier
                    .sFontSize(14)
                    .color(AppColors.Primary)
            )
        }
    }
}

/** Pill-shaped Cancel button matching the Figma modal footer. */
@Composable
private fun ModalCancelButton(
    onClick: () -> Unit,
    enabled: Boolean = true
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(28)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .opacity(if (enabled) 1.0 else 0.6)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("padding", "0 ${s(28)}")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            t.commonCancel,
            modifier = Modifier
                .sFontSize(15)
                .fontWeight(FontWeight.Medium)
                .color(AppColors.Text.Secondary)
        )
    }
}

/** Solid blue pill Create Event button with drop shadow. */
@Composable
private fun ModalCreateButton(
    text: String,
    onClick: () -> Unit,
    enabled: Boolean = true,
    // Mobile sheet stretches the button to the full sheet width; desktop keeps
    // it auto-sized in the right-aligned footer.
    fullWidth: Boolean = false,
    // Coin charge shown wallet-style after the label: coin glyph first, then
    // "× 20". Null hides the price block entirely.
    coinPrice: String? = null
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
            .sHeight(44)
            .sBorderRadius(28)
            .backgroundColor(sportColors.primary)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .opacity(if (enabled) 1.0 else 0.6)
            .styleModifier {
                property("padding", "0 ${s(28)}")
                property("display", if (fullWidth) "flex" else "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                // The button's only content is a one-line label, so inside the
                // mobile sheet's flex scroll column flex-shrink would squash its
                // 44px height. Pin it.
                property("flex-shrink", "0")
                property("box-shadow", "0 6px 16px ${sportColors.primary.toRgbaString(0.35f)}")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center
    ) {
        Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                text,
                modifier = Modifier
                    .sFontSize(15)
                    .fontWeight(FontWeight.SemiBold)
                    .color(sportColors.onPrimary)
            )
            if (coinPrice != null) {
                CoinIcon(30)
                SpanText(
                    "× $coinPrice",
                    modifier = Modifier
                        .sFontSize(15)
                        .fontWeight(FontWeight.SemiBold)
                        .color(sportColors.onPrimary)
                )
            }
        }
    }
}

/** Cost input — pill with € prefix matching the Figma design. */
@Composable
private fun CostInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    Column(modifier = modifier.fillMaxWidth().sGap(6)) {
        FieldLabel(t.eventCostLabel)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(44)
                .sBorderRadius(28)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("padding-left", s(14))
                    property("padding-right", s(8))
                    property("box-sizing", "border-box")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                "€",
                modifier = Modifier
                    .sFontSize(16)
                    .color(Color("rgba(153,177,191,0.7)"))
                    .styleModifier { property("margin-right", s(6)) }
            )
            org.jetbrains.compose.web.dom.Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                value(value)
                onInput { onValueChange(it.value) }
                attr("placeholder", "0.00")
                attr("inputmode", "decimal")
                style {
                    width(100.percent)
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("font-size", s(16))
                    property("font-family", "inherit")
                    property("color", "#bed6f5")
                    property("padding", "0")
                }
            }
        }
    }
}

/** Private toggle row with optional hint copy when PRO is on. */
@Composable
private fun PrivateField(
    isPrivate: Boolean,
    onPrivateChange: (Boolean) -> Unit,
    isPro: Boolean,
    showInfo: Boolean,
    onToggleInfo: () -> Unit,
    onDismissInfo: () -> Unit,
    // When true the row fills the width and pushes the toggle to the far right
    // (label + info on the left). Used by the mobile sheet.
    spread: Boolean = false,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .styleModifier { property("position", "relative") }
            .then(if (spread) Modifier.fillMaxWidth() else Modifier)
            .sGap(8)
    ) {
        Row(
            modifier = Modifier
                .sGap(8)
                .then(if (spread) Modifier.fillMaxWidth() else Modifier),
            horizontalArrangement = if (spread) Arrangement.SpaceBetween else Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.eventPrivateLabel,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Secondary)
                        .cursor(Cursor.Pointer)
                        .onClick { onToggleInfo() }
                )
                Icon(
                    src = AppIcons.InfoCircle,
                    modifier = Modifier.sSize(16)
                        .cursor(Cursor.Pointer)
                        .onClick { onToggleInfo() }
                        .styleModifier { property("opacity", "0.6") }
                )
            }
            Toggle(checked = isPrivate, onCheckedChange = onPrivateChange)
        }

        if (showInfo) {
            PrivateInfoTooltip(onDismiss = onDismissInfo)
        }
    }
}

@Composable
private fun PrivateInfoTooltip(onDismiss: () -> Unit) {
    val t = LocalAppStrings.current
    // Dimming backdrop to close on click-away. z-index 10000 keeps us above
    // Leaflet's own stacking contexts (controls, panes) so the map dims too.
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(10000)
            .styleModifier { property("background", "rgba(0, 0, 0, 0.55)") }
            .onClick { onDismiss() }
    )

    // Tooltip card
    Column(
        modifier = Modifier
            .position(Position.Absolute)
            .zIndex(10001)
            .styleModifier {
                property("bottom", "calc(100% + ${s(8)})")
                property("left", "0")
                property("background", "#09142d")
                property("border-radius", s(18))
                property("padding", "${s(14)} ${s(18)} ${s(22)} ${s(18)}")
                property("width", s(324))
                property("box-sizing", "border-box")
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Text section
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(12)
                .sPaddingBottom(12)
                .styleModifier {
                    property("border-bottom", "1px solid rgba(148, 149, 152, 0.15)")
                }
        ) {
            SpanText(
                t.eventPrivateLabel,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("line-height", s(26))
                        property("letter-spacing", "0.2px")
                    }
            )
            SpanText(
                t.eventPrivateInfoDescription,
                modifier = Modifier
                    .sFontSize(14)
                    .color(Color("#99b1bf"))
                    .styleModifier {
                        property("line-height", s(18))
                        property("letter-spacing", "0.028px")
                    }
            )
        }

        // Player card preview — Figma 7002:566885 (avatar + bottom rating only)
        Box(
            modifier = Modifier.sMarginTop(26),
            contentAlignment = Alignment.Center
        ) {
            MiniPlayerPreviewCard(DemoPlayers.Football.first())
        }
    }
}

/**
 * Compact preview card used inside info tooltips — renders the player photo
 * with a small rating/position pill at the bottom and an "approve / decline"
 * action row underneath. Matches Figma 7002:566885.
 */
@Composable
private fun MiniPlayerPreviewCard(demo: DemoPlayer) {
    val t = LocalAppStrings.current
    // One gradient frame contains everything — photo card on top, action
    // buttons stacked below, all inside the rounded border (Figma 7002:566885).
    Column(
        modifier = Modifier
            .styleModifier {
                property("width", s(132))
                property("padding", "${s(4)} ${s(6)}")
                property("border-radius", s(12))
                property(
                    "background",
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(48,108,206,0.2) 100%)"
                )
                property("border", "0.5px solid rgba(255,255,255,0.4)")
                property("box-sizing", "border-box")
                property("gap", s(2))
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier.styleModifier { property("width", "100%") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Inner card area — player photo + rating overlay (Figma 97×147)
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "relative")
                    property("width", s(97))
                    property("height", s(147))
                }
            ) {
                // Player photo — fills the card
                Image(
                    src = demo.avatarUrl,
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("border-radius", s(10))
                    },
                    alt = demo.name
                )
                // Info container — name + mini rating card (Figma left=29, top=78)
                Column(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("left", "50%")
                        property("top", s(78))
                        property("transform", "translateX(-50%)")
                        property("width", s(50))
                        property("align-items", "center")
                        property("gap", s(2))
                    }
                ) {
                    // Player name
                    SpanText(
                        demo.name,
                        modifier = Modifier.styleModifier {
                            property("font-family", "'IBM Plex Sans', sans-serif")
                            property("font-weight", "700")
                            property("font-size", s(8))
                            property("color", "#1e7ff5")
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.08px")
                            property("text-align", "center")
                            property("white-space", "nowrap")
                            property("line-height", s(14))
                        }
                    )
                    // Mini rating card — uses the dedicated rating-frame asset
                    // from the CDN (`cards/<sport>/card_rating.png`)
                    Box(
                        modifier = Modifier.styleModifier {
                            property("position", "relative")
                            property("width", s(28))
                            property("height", s(42))
                        }
                    ) {
                        Image(
                            src = demo.ratingCardUrl,
                            modifier = Modifier.styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("width", "100%")
                                property("height", "100%")
                                property("pointer-events", "none")
                                property("object-fit", "contain")
                            },
                            alt = ""
                        )
                        Column(
                            modifier = Modifier.styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                        ) {
                            SpanText(
                                demo.rating.toString(),
                                modifier = Modifier.styleModifier {
                                    property("font-family", "'IBM Plex Sans', sans-serif")
                                    property("font-weight", "600")
                                    property("font-size", s(12))
                                    property("color", "#1e7ff5")
                                    property("line-height", s(14))
                                    property("letter-spacing", "0.12px")
                                }
                            )
                            Box(
                                modifier = Modifier.styleModifier {
                                    property("width", s(13))
                                    property("height", "1px")
                                    property("margin", "${s(1)} 0")
                                    property("background", "#1e7ff5")
                                    property("opacity", "0.85")
                                }
                            )
                            SpanText(
                                demo.position,
                                modifier = Modifier.styleModifier {
                                    property("font-family", "'IBM Plex Sans', sans-serif")
                                    property("font-weight", "600")
                                    property("font-size", s(8))
                                    property("color", "#1e7ff5")
                                    property("text-transform", "uppercase")
                                    property("letter-spacing", "-0.08px")
                                    property("line-height", "1")
                                }
                            )
                        }
                    }
                }
            }
        }

        // Action buttons — stacked directly under the card, inside the frame
        Row(
            modifier = Modifier.styleModifier {
                property("gap", s(2))
                property("height", s(36))
                property("align-items", "center")
            }
        ) {
            Box(
                modifier = Modifier
                    .sSize(40)
                    .cursor(Cursor.Pointer)
                    .styleModifier { property("display", "flex"); property("align-items", "center"); property("justify-content", "center") }
            ) {
                Image(
                    src = AppIcons.BtnDelete,
                    modifier = Modifier.sWidth(32).sHeight(14),
                    alt = t.commonDelete
                )
            }
            Box(
                modifier = Modifier
                    .sSize(40)
                    .cursor(Cursor.Pointer)
                    .styleModifier { property("display", "flex"); property("align-items", "center"); property("justify-content", "center") }
            ) {
                Image(
                    src = AppIcons.BtnConfirm,
                    modifier = Modifier.sWidth(32).sHeight(14),
                    alt = t.commonConfirm
                )
            }
        }
    }
}
